import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useAuthStore } from '@/entities/user';
import { routeForStatus } from '@/features/auth';
import { exchangeSocialCode } from '@/shared/api';
import { getErrorMessage } from '@/shared/lib';
import { buttonVariants, cn } from '@/shared/ui';
import { AuthShell } from '@/widgets/auth-shell';

/** 콜백의 ?error= 코드 → 사용자 안내. 서버는 세부 사유를 URL 로 노출하지 않는다. */
const ERROR_MESSAGES: Readonly<Record<string, string>> = {
  EMAIL_REQUIRED:
    '소셜 계정에서 이메일을 제공받지 못했습니다. 이메일 제공에 동의하거나, 이메일로 직접 가입해 주세요.',
  EMAIL_NOT_VERIFIED:
    '같은 이메일의 계정이 이미 있지만 아직 이메일 인증 전입니다. 기존 계정으로 로그인해 이메일 인증을 먼저 완료해 주세요.',
  ACCOUNT_BLOCKED: '이용이 제한된 계정입니다.',
  OAUTH_FAILED: '소셜 로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.',
};

/**
 * 소셜 콜백 도착 페이지(/oauth/callback). 서버가 ?code=(성공) 또는 ?error= 를 붙여
 * 리다이렉트한다 — code 는 일회용(60초)이라 즉시 세션으로 교환한다. (설계 §7)
 */
export const OauthCallbackPage = (): ReactElement => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((state) => state.setSession);
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  // StrictMode 이중 마운트에서 code 가 두 번 소비되지 않게 1회만 실행한다.
  const hasFired = useRef(false);
  // 실제 언마운트 후의 상태 갱신 · 내비게이션을 막는 플래그. 이펙트 클로저 변수로
  // 두면 StrictMode 의 시뮬레이션 언마운트(클린업)가 true 로 만든 뒤 재실행이
  // hasFired 에 막혀 복구되지 못해, 개발 모드에서 내비게이션이 영영 생략된다 —
  // ref 로 두고 이펙트 진입 시마다 복구한다.
  const isUnmounted = useRef(false);

  const code = params.get('code');
  const errorCode = params.get('error');

  useEffect(() => {
    isUnmounted.current = false;

    if (!code || hasFired.current) {
      return () => {
        isUnmounted.current = true;
      };
    }

    hasFired.current = true;

    // code 는 일회용이라 fetch 자체는 중단하지 않되, 언마운트 후에는
    // 상태 갱신 · 내비게이션을 하지 않는다. (죽은 화면 위 라우팅 방지)
    exchangeSocialCode(code)
      .then((session) => {
        // 세션 반영은 언마운트와 무관하게 유효하다 — 교환이 성공했으면 로그인된 것이다.
        setSession(session);

        if (!isUnmounted.current) {
          // 소셜 로그인의 성공은 "온보딩 진입"일 수 있다 — 상태로 라우팅한다. (설계 §2-3)
          void navigate(routeForStatus(session.user.status), { replace: true });
        }
      })
      .catch((error) => {
        if (!isUnmounted.current) {
          setExchangeError(getErrorMessage(error, '소셜 로그인에 실패했습니다.'));
        }
      });

    return () => {
      isUnmounted.current = true;
    };
  }, [code, navigate, setSession]);

  const message = errorCode
    ? (ERROR_MESSAGES[errorCode] ?? ERROR_MESSAGES.OAUTH_FAILED)
    : exchangeError;

  if (message) {
    return (
      <AuthShell title="소셜 로그인" description="로그인을 완료하지 못했습니다.">
        <p
          role="alert"
          className="border-l-2 border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {message}
        </p>
        <div className="mt-6 flex flex-col items-center gap-4 text-sm">
          <Link
            to="/sign-in"
            className={cn(
              buttonVariants(),
              'h-14 w-full rounded-none bg-primary text-[15px] font-semibold text-white hover:bg-primary/90 dark:text-primary-foreground',
            )}
          >
            로그인으로 이동
          </Link>
          {errorCode === 'EMAIL_REQUIRED' ? (
            <Link
              to="/sign-up"
              className="font-semibold text-foreground underline underline-offset-4 hover:opacity-70"
            >
              이메일로 가입하기
            </Link>
          ) : null}
        </div>
      </AuthShell>
    );
  }

  if (!code) {
    return (
      <AuthShell title="소셜 로그인" description="잘못된 접근입니다.">
        <Link
          to="/sign-in"
          className="text-sm font-semibold text-foreground underline underline-offset-4 hover:opacity-70"
        >
          로그인으로 이동
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="소셜 로그인" description="로그인을 완료하고 있습니다…">
      <p className="text-sm text-muted-foreground">잠시만 기다려 주세요.</p>
    </AuthShell>
  );
};
